import { useState, useEffect, useRef, useCallback } from 'react';
import { HiPrinter, HiDocumentText, HiChartBar, HiChevronDown, HiExclamationCircle, HiInbox, HiTable } from 'react-icons/hi';
import { useAlert } from '../contexts/AlertContext';
import { getUser } from '../utils/auth';
import { formatDate } from '../utils/reportFormat';
import printCategoryReport from '../utils/printCategoryReport';
import exportCategoryReport from '../utils/exportCategoryReport';
import ReportFilters, { defaultFilters } from '../components/reports/ReportFilters';
import ReportKPIs from '../components/reports/ReportKPIs';
import CategoryCharts from '../components/reports/CategoryCharts';
import CategoryTable from '../components/reports/CategoryTable';

const API_BASE = '/api/admin';
const FILTER_DEBOUNCE_MS = 350;

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function PrintMenu({ disabled, onPrint }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (e.type === 'keydown' ? e.key === 'Escape' : !ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  const choose = (mode) => {
    setOpen(false);
    onPrint(mode);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gold-400 text-primary-700 text-sm font-bold shadow-lg shadow-gold-400/30 hover:bg-gold-300 transition-all active:scale-95 disabled:opacity-50 disabled:shadow-none"
      >
        <HiPrinter className="w-5 h-5" />
        طباعة
        <HiChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div role="menu" className="absolute left-0 z-30 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-primary-100/60 p-2 animate-slideDown">
          <button
            role="menuitem"
            type="button"
            onClick={() => choose('invoice')}
            className="w-full flex items-start gap-3 p-3 rounded-xl text-right hover:bg-primary-50/60 transition-colors"
          >
            <div className="p-2 rounded-lg bg-primary-50 text-primary-600"><HiDocumentText className="w-5 h-5" /></div>
            <div>
              <p className="text-sm font-bold text-gray-800">فاتورة مبيعات</p>
              <p className="text-xs text-gray-400">الكميات والأسعار والإجماليات فقط</p>
            </div>
          </button>
          <button
            role="menuitem"
            type="button"
            onClick={() => choose('financial')}
            className="w-full flex items-start gap-3 p-3 rounded-xl text-right hover:bg-primary-50/60 transition-colors"
          >
            <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600"><HiChartBar className="w-5 h-5" /></div>
            <div>
              <p className="text-sm font-bold text-gray-800">تقرير مالي كامل</p>
              <p className="text-xs text-gray-400">يشمل التكلفة والربح التقديري — للاستخدام الداخلي</p>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}

function ExportButton({ disabled, exporting, onExport }) {
  return (
    <button
      type="button"
      onClick={onExport}
      disabled={disabled || exporting}
      aria-busy={exporting}
      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-bold shadow-lg shadow-emerald-900/20 hover:bg-emerald-500 transition-all active:scale-95 disabled:opacity-50 disabled:shadow-none"
    >
      {exporting ? (
        <span className="w-5 h-5 rounded-full border-2 border-white/40 border-t-white animate-spin" aria-hidden="true" />
      ) : (
        <HiTable className="w-5 h-5" />
      )}
      {exporting ? 'جاري التصدير…' : 'تصدير Excel'}
    </button>
  );
}

function ReportSkeleton() {
  return (
    <div className="space-y-6 animate-pulse" aria-busy="true" aria-label="جاري تحميل التقرير">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-gray-200/70 rounded-2xl" />)}
      </div>
      <div className="h-16 bg-gray-200/70 rounded-2xl" />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 h-80 bg-gray-200/70 rounded-2xl" />
        <div className="lg:col-span-3 h-80 bg-gray-200/70 rounded-2xl" />
      </div>
      <div className="h-72 bg-gray-200/70 rounded-2xl" />
    </div>
  );
}

export default function FinancialReport() {
  const showAlert = useAlert();
  const [filters, setFilters] = useState(defaultFilters);
  const [categories, setCategories] = useState([]);
  const [cashiers, setCashiers] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [report, setReport] = useState(null);
  // The filters the current report was built from, so print/export labels never
  // describe a newer filter change whose request is still debouncing.
  const [reportFilters, setReportFilters] = useState(filters);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  const hasReport = useRef(false);

  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch(`${API_BASE}/categories`, { headers }).then((r) => (r.ok ? r.json() : [])),
      fetch(`${API_BASE}/users/cashiers`, { headers }).then((r) => (r.ok ? r.json() : [])),
    ])
      .then(([cats, users]) => {
        setCategories(Array.isArray(cats) ? [...cats].sort((a, b) => a.name.localeCompare(b.name, 'ar')) : []);
        setCashiers(Array.isArray(users) ? users : []);
      })
      .catch(() => showAlert({ type: 'error', title: 'خطأ', message: 'تعذر تحميل قوائم التصنيفات والبائعين' }))
      .finally(() => setLoadingOptions(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced so ticking several categories fires one request, and aborted so
  // a slow earlier response can never overwrite a newer one.
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      if (hasReport.current) setRefreshing(true);
      else setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_BASE}/sales/category-report`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            ...filters,
            cashierId: filters.cashierId || undefined,
          }),
          signal: controller.signal,
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || `فشل في تحميل التقرير (${res.status})`);
        }
        setReport(await res.json());
        setReportFilters(filters);
        hasReport.current = true;
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    }, FILTER_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [filters, reloadKey]);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const filterLabels = () => {
    const categoryNames = reportFilters.categoryIds
      .map((id) => categories.find((c) => c.id === id)?.name)
      .filter(Boolean);
    return {
      categories: categoryNames.length ? categoryNames.join('، ') : 'كل التصنيفات',
      cashier: cashiers.find((c) => c.id === reportFilters.cashierId)?.name || 'كل البائعين',
      source: reportFilters.source,
    };
  };

  const handlePrint = (mode) => {
    const opened = printCategoryReport(report, {
      mode,
      issuedBy: getUser()?.name,
      filterLabels: filterLabels(),
    });
    if (!opened) {
      showAlert({ type: 'warning', title: 'تعذر فتح نافذة الطباعة', message: 'يرجى السماح بالنوافذ المنبثقة لهذا الموقع ثم المحاولة مجدداً' });
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportCategoryReport(report, { issuedBy: getUser()?.name, filterLabels: filterLabels() });
    } catch {
      showAlert({ type: 'error', title: 'تعذر التصدير', message: 'حدث خطأ أثناء إنشاء ملف Excel، يرجى المحاولة مجدداً' });
    } finally {
      setExporting(false);
    }
  };

  const isEmpty = report && report.categories.length === 0;
  const actionsDisabled = !report || isEmpty || loading;

  return (
    <div className="space-y-6">
      <div className="relative z-30 rounded-2xl bg-gradient-to-l from-primary-700 via-primary-600 to-primary-500 p-5 md:p-6 text-white shadow-lg animate-fadeIn">
        <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
          <div className="absolute -left-10 -top-10 w-40 h-40 rounded-full bg-gold-400/10" />
          <div className="absolute left-24 -bottom-12 w-32 h-32 rounded-full bg-gold-400/10" />
        </div>
        <div className="relative flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <p className="text-xs text-gold-300 font-semibold mb-1">التقارير المالية</p>
            <h2 className="text-xl md:text-2xl font-bold">تقرير المبيعات حسب التصنيف</h2>
            <p className="text-sm text-primary-100 mt-1">
              {report
                ? `${formatDate(report.startDate)} — ${formatDate(report.endDate)}`
                : 'اختر الفترة والتصنيفات لعرض التقرير'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ExportButton disabled={actionsDisabled || refreshing} exporting={exporting} onExport={handleExport} />
            <PrintMenu disabled={actionsDisabled} onPrint={handlePrint} />
          </div>
        </div>
      </div>

      <ReportFilters
        filters={filters}
        onChange={setFilters}
        categories={categories}
        cashiers={cashiers}
        loadingOptions={loadingOptions}
        refreshing={refreshing}
        onRefresh={refresh}
      />

      {error && (
        <div role="alert" className="flex items-center gap-3 p-4 rounded-2xl bg-red-50 border border-red-200 animate-fadeIn">
          <HiExclamationCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-700">فشل في تحميل التقرير</p>
            <p className="text-xs text-red-500 mt-0.5">{error}</p>
          </div>
          <button
            type="button"
            onClick={refresh}
            className="px-3 py-1.5 rounded-lg bg-red-100 text-red-700 text-xs font-semibold hover:bg-red-200 transition-colors"
          >
            إعادة المحاولة
          </button>
        </div>
      )}

      {loading && !report && <ReportSkeleton />}

      {report && (
        <div className={`space-y-6 transition-opacity duration-200 ${refreshing ? 'opacity-60' : ''}`}>
          {isEmpty ? (
            <div className="bg-white rounded-2xl shadow-sm border border-primary-100/40 flex flex-col items-center justify-center py-20 px-6 text-center animate-fadeIn">
              <div className="w-16 h-16 rounded-2xl bg-primary-50 flex items-center justify-center mb-4">
                <HiInbox className="w-8 h-8 text-primary-300" />
              </div>
              <p className="text-base font-bold text-gray-700">لا توجد مبيعات مطابقة</p>
              <p className="text-sm text-gray-400 mt-1">جرّب توسيع الفترة الزمنية أو تغيير التصنيفات والمعايير المحددة</p>
            </div>
          ) : (
            <>
              <ReportKPIs summary={report.summary} bySource={report.bySource} />
              <CategoryCharts
                categories={report.categories}
                dailyTrend={report.dailyTrend}
                totalRevenue={report.summary.revenue}
              />
              <CategoryTable categories={report.categories} summary={report.summary} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
